import {
  Model,
  UIAnchor,
  UICheckbox,
  UIDate,
  UIField,
  UIFormatTemplateSelection,
  UIRadio,
  UIRange,
  UISelect,
  UITable,
} from "@alphascriptsolutions/client-core";
import { ModelIndexes } from "../../types/addContent";
import { getAllImageButtonValues } from "./addPanelsTransition";
import { buttonsValue } from "../imageSelectButton";
import { findCompanyByName } from "./companyDetailsLocalStorage";
import { Company } from "../../state/entities";
import { useSharePricePerformanceStore } from "../../state/sharePricePerformanceStore";
import { getSelectedShape } from "./selectedPlaceHolderLocalStorage";

export function setAllPanelsFieldValue(allModels: Model[]) {
  let allPanelsData = [];

  allModels.forEach((model: Model, i: number) => {
    const {
      isValidIndexation,
      isCustomBuiltIndice,
      isConstantLine,
      isValidSummaryTable,
      isValidSmallLineChart,
      isValidCallOutBoxes,
      isValidBackgroundShading,
      isValidVerticalLine,
    } = getAllImageButtonValues(allModels);

    if (i === ModelIndexes.CONTENT_LIBRARY) {
      let tempValues = {};
      const imageButtonNames = [
        "sharePricePerformanceLineChart",
        "sharePricePerformanceColumnChart",
        "sharePricePerformanceBarChart",
        "sharePricePerformanceTable",
      ];
      const chartType = buttonsValue(imageButtonNames, model);
      tempValues = {
        chartType: chartType,
      };

      allPanelsData.push(tempValues);
    }

    if (i === ModelIndexes.TABLE_MODEL_INDEX) {
      let tempValues = {};
      const companiesOnChartTable = model.getByName<UITable>("companiesOnChartTable");
      const timePeriodInitialInputs = model.getByName<UIRange>("timePeriodInitialInputs");
      // const currencySelection = model.getByName<UISelect>("currencySelectionSlot");

      const imageButtonNames = ["percentageChangeChart", "actualTradedValueChart"];
      const percentageChart = buttonsValue(imageButtonNames, model);

      const imageButtonConstantValueLineNames = ["addCustomBuiltIndices", "noCustomBuiltIndices"];
      const customBuildIndex = buttonsValue(imageButtonConstantValueLineNames, model);

      const imageButtonStaticLineNames = ["addConstantLineSelection", "noConstantLineSelection"];
      const staticLine = buttonsValue(imageButtonStaticLineNames, model);

      const currencySelectionSlotValue = localStorage.getItem("currencySelectionSlotValue") ?? "USD";

      tempValues = {
        companies: companiesOnChartTable.value,
        timePeriod: timePeriodInitialInputs.value,
        currency: currencySelectionSlotValue,
        percentageChart: percentageChart === imageButtonNames[0],
        customBuildIndex: customBuildIndex === imageButtonConstantValueLineNames[0],
        staticLine: staticLine === imageButtonStaticLineNames[0],
      };

      allPanelsData.push(tempValues);
    }

    if (i === ModelIndexes.INDEXATION_MODEL_INDEX) {
      let tempValues = {};
      if (isValidIndexation !== "Yes") {
        allPanelsData.push(tempValues);
        return;
      }

      const indexStartingPoint = model.getByName<UIRadio>("indexStartingPoint");
      if (indexStartingPoint.value === "Company Share Price") {
        const companyNames = model.getByName<UIRadio>("companyNames");
        const companyData: Company = findCompanyByName(companyNames.value);
        if (companyData) {
          tempValues = {
            indexStartingPoint: indexStartingPoint.value,
            company: {
              name: companyData.companyNameAndTicker,
              ticker: companyData.company.ticker,
              exchange: companyData.company.exchange,
            },
          };
        }
      }

      if (indexStartingPoint.value === "Value") {
        const value = model.getByName<UIField>("SelectedValue");
        tempValues = {
          indexStartingPoint: indexStartingPoint.value,
          value: value.value,
        };
      }
      allPanelsData.push(tempValues);
    }

    if (i === ModelIndexes.CUSTOM_BUILD_INDEX) {
      let tempValues = {};

      if (isCustomBuiltIndice !== "Yes") {
        allPanelsData.push(tempValues);
        return;
      }

      const indexName = model.getByName<UIField>("indexName");
      const constituentContributionApproach = model.getByName<UISelect>("constituentContributionApproach");
      const defineIndexContituents = model.getByName<UITable>("defineIndexContituents");
      const indexContainer = model.getByName<UIAnchor>("indexContainer");

      tempValues = {
        index: {
          id: 1,
          indexName: indexName.value,
          constituentContributionApproach: constituentContributionApproach.value,
          defineIndexContituents: defineIndexContituents.value,
        },
      };

      indexContainer.children.map((miniMoel, index) => {
        const indexName = miniMoel.getByName<UIField>(`indexName${index + 1}`);
        const constituentContributionApproach = miniMoel.getByName<UISelect>(
          `constituentContributionApproach${index + 1}`
        );
        const defineIndexContituents = miniMoel.getByName<UITable>(`defineIndexContituents${index + 1}`);

        tempValues = {
          ...tempValues,
          [`index${index + 1}`]: {
            id: index + 2,
            indexName: indexName.value,
            constituentContributionApproach: constituentContributionApproach.value,
            defineIndexContituents: defineIndexContituents.value,
          },
        };
      });

      allPanelsData.push(tempValues);
    }

    if (i === ModelIndexes.CONSTANT_LINE) {
      let tempValues = {};

      if (isConstantLine !== "Yes") {
        allPanelsData.push(tempValues);
        return;
      }

      let nestedTempValues = {};

      const lineName = model.getByName<UIField>("lineName");
      const calculationApproach = model.getByName<UISelect>("calculationApproach");

      nestedTempValues = {
        id: 1,
        lineName: lineName ? lineName.value : null,
        calculationApproach: calculationApproach ? calculationApproach.value : null,
      };

      if (calculationApproach && calculationApproach.value === "Input Value") {
        const InputvalueApprach = model.getByName<UIField>("InputvalueApprach");

        nestedTempValues = {
          ...nestedTempValues,
          value: InputvalueApprach ? InputvalueApprach.value : null,
        };
      }

      if (calculationApproach && calculationApproach.value === "Derived Value") {
        const derivedDataCalculationApproach = model.getByName<UIRadio>("derivedDataCalculationApproach");

        nestedTempValues = {
          ...nestedTempValues,
          derivedDataCalculationApproach: derivedDataCalculationApproach ? derivedDataCalculationApproach.value : null,
        };

        if (derivedDataCalculationApproach && derivedDataCalculationApproach.value === "Time Series") {
          const timePeriodTimeSeries = model.getByName<UIRange>("timePeriodTimeSeries");
          const CalculationRadio = model.getByName<UISelect>("CalculationRadio");
          const radioCompanyNamesTimeSeries = model.getByName<UISelect>("radioCompanyNamesTimeSeries");

          const companyData: Company = findCompanyByName(radioCompanyNamesTimeSeries.value);

          nestedTempValues = {
            ...nestedTempValues,
            timePeriod: timePeriodTimeSeries ? timePeriodTimeSeries.value : null,
            calculation: CalculationRadio ? CalculationRadio.value : null,
            company: {
              name: companyData.companyNameAndTicker,
              ticker: companyData.company.ticker,
              exchange: companyData.company.exchange,
            },
          };
        }

        if (derivedDataCalculationApproach && derivedDataCalculationApproach.value === "Point-in-Time") {
          const staticLineDateInitialInput = model.getByName<UIDate>("staticLineDateInitialInput");
          const staticLine1Date = model.getByName<UIField>("staticLine1Date");
          const radioCompanyNamesPointInTime = model.getByName<UISelect>("radioCompanyNamesPointInTime");

          const companyData: Company = findCompanyByName(radioCompanyNamesPointInTime.value);

          nestedTempValues = {
            ...nestedTempValues,
            Date: staticLineDateInitialInput ? staticLineDateInitialInput.value : null,
            premiumToDate: staticLine1Date ? staticLine1Date.value : null,
            company: {
              name: companyData.companyNameAndTicker,
              ticker: companyData.company.ticker,
              exchange: companyData.company.exchange,
            },
          };
        }
      }

      tempValues = {
        ...tempValues,
        line: nestedTempValues,
      };

      const lineContainer = model.getByName<UIAnchor>("lineContainer");

      lineContainer.children.map((miniMoel, index) => {
        let nestedTempValuesMini = {};
        const miniLineName = miniMoel.getByName<UIField>(`lineName${index + 1}`);
        const miniCalculationApproach = miniMoel.getByName<UISelect>(`calculationApproach${index + 1}`);

        nestedTempValuesMini = {
          id: index + 2,
          lineName: miniLineName ? miniLineName.value : null,
          calculationApproach: miniCalculationApproach ? miniCalculationApproach.value : null,
        };

        if (miniCalculationApproach && miniCalculationApproach.value === "Input Value") {
          const miniInputValueApproach = miniMoel.getByName<UIField>(`InputvalueApprach${index + 1}`);

          nestedTempValuesMini = {
            ...nestedTempValuesMini,
            value: miniInputValueApproach ? miniInputValueApproach.value : null,
          };
        }

        if (miniCalculationApproach && miniCalculationApproach.value === "Derived Value") {
          const miniDerivedDataCalculationApproach = miniMoel.getByName<UIRadio>(
            `derivedDataCalculationApproach${index + 1}`
          );

          nestedTempValuesMini = {
            ...nestedTempValuesMini,
            derivedDataCalculationApproach: miniDerivedDataCalculationApproach
              ? miniDerivedDataCalculationApproach.value
              : null,
          };

          if (miniDerivedDataCalculationApproach && miniDerivedDataCalculationApproach.value === "Time Series") {
            const miniTimePeriodTimeSeries = miniMoel.getByName<UIRange>(`timePeriodTimeSeries${index + 1}`);
            const miniCalculationRadio = miniMoel.getByName<UISelect>(`CalculationRadio${index + 1}`);
            const miniRadioCompanyNamesTimeSeries = miniMoel.getByName<UISelect>(
              `radioCompanyNamesTimeSeries${index + 1}`
            );

            const companyData: Company = findCompanyByName(miniRadioCompanyNamesTimeSeries.value);
            nestedTempValuesMini = {
              ...nestedTempValuesMini,
              timePeriod: miniTimePeriodTimeSeries ? miniTimePeriodTimeSeries.value : null,
              calculation: miniCalculationRadio ? miniCalculationRadio.value : null,
              company: {
                name: companyData.companyNameAndTicker,
                ticker: companyData.company.ticker,
                exchange: companyData.company.exchange,
              },
            };
          }

          if (miniDerivedDataCalculationApproach && miniDerivedDataCalculationApproach.value === "Point-in-Time") {
            const miniStaticLineDateInitialInput = miniMoel.getByName<UIDate>(`staticLineDateInitialInput${index + 1}`);
            const miniStaticLine1Date = miniMoel.getByName<UIField>(`staticLine1Date${index + 1}`);
            const miniRadioCompanyNamesPointInTime = miniMoel.getByName<UISelect>(
              `radioCompanyNamesPointInTime${index + 1}`
            );

            const companyData: Company = findCompanyByName(miniRadioCompanyNamesPointInTime.value);

            nestedTempValuesMini = {
              ...nestedTempValuesMini,
              Date: miniStaticLineDateInitialInput ? miniStaticLineDateInitialInput.value : null,
              premiumToDate: miniStaticLine1Date ? miniStaticLine1Date.value : null,
              company: {
                name: companyData.companyNameAndTicker,
                ticker: companyData.company.ticker,
                exchange: companyData.company.exchange,
              },
            };
          }
        }

        tempValues = {
          ...tempValues,
          [`line${index + 1}`]: nestedTempValuesMini,
        };
      });

      allPanelsData.push(tempValues);
    }

    if (i === ModelIndexes.CUSTOMIZE_OPTIONS) {
      let tempValues = {};

      const secondaryVerticalAxisButtonNames = [
        "addsecondaryVerticalAxisColumnChart",
        "nosecondaryVerticalAxisColumnChart",
      ];
      const tradingVolume = buttonsValue(secondaryVerticalAxisButtonNames, model);

      const summaryTableImageNames = ["addsummaryTable", "nosummaryTable"];
      const summaryTable = buttonsValue(summaryTableImageNames, model);

      const smallLineChartImageNames = ["addSmallLineChart", "nosmallLineChart"];
      const smallLineChart = buttonsValue(smallLineChartImageNames, model);

      const callOutBoxesImageNames = ["addCallOutBoxes", "noCallOutBoxes"];
      const callOutBoxes = buttonsValue(callOutBoxesImageNames, model);

      const backgroundShadingImageNames = ["addBackgroundShading", "noBackgroundShading"];
      const backgroundShading = buttonsValue(backgroundShadingImageNames, model);

      const verticalLineImageNames = ["addVerticalLine", "noVerticalLine"];
      const verticalLine = buttonsValue(verticalLineImageNames, model);

      tempValues = {
        tradingVolume: tradingVolume === secondaryVerticalAxisButtonNames[0],
        summaryTable: summaryTable === summaryTableImageNames[0],
        smallLineChart: smallLineChart === smallLineChartImageNames[0],
        callOutBoxes: callOutBoxes === callOutBoxesImageNames[0],
        backgroundShading: backgroundShading === backgroundShadingImageNames[0],
        verticalLine: verticalLine === verticalLineImageNames[0],
      };

      allPanelsData.push(tempValues);
    }

    if (i === ModelIndexes.SUMMARY_TABLE) {
      let tempValues = {};

      if (isValidSummaryTable !== "Yes") {
        allPanelsData.push(tempValues);
        return;
      }

      if (isValidSummaryTable === "Yes") {
        const checkboxCompaniesIncluded = model.getByName<UICheckbox>("checkboxCompaniesIncluded");
        const rowHeadings = model.getByName<UISelect>("rowHeadings");
        const testTable = model.getByName<UITable>("periodSummaryTable");
        const customIndicesIncluded = model.getByName<UICheckbox>("customIndicesIncluded");
        const companiesNames = checkboxCompaniesIncluded.value;
        const selectedCompanies = companiesNames
          .map((companyName: string) => {
            const companyData: Company = findCompanyByName(companyName);
            if (companyData) {
              return {
                name: companyData.companyNameAndTicker,
                ticker: companyData.company.ticker,
                exchange: companyData.company.exchange,
              };
            } else {
              return null;
            }
          })
          .filter(Boolean);

        const filteredCustomIndices = Object.values(allPanelsData[ModelIndexes.CUSTOM_BUILD_INDEX]).filter((item) => {
          const panelItem = item as { indexName: string };
          return panelItem.indexName && customIndicesIncluded.value.includes(panelItem.indexName);
        });

        tempValues = {
          ...tempValues,
          rowDataCategory: rowHeadings.value,
          summaryPeriodTable: testTable.value,
          summaryCompaniesIndices: selectedCompanies,
          customIndicesNames: customIndicesIncluded.value,
          customIndices: filteredCustomIndices,
        };
      }

      allPanelsData.push(tempValues);
    }

    if (i === ModelIndexes.SMALL_LINE_CHART) {
      let tempValues = {};

      if (isValidSmallLineChart !== "Yes") {
        allPanelsData.push(tempValues);
        return;
      }

      if (isValidSmallLineChart === "Yes") {
        const smallLineChartTimePeriod = model.getByName<UIRange>("smallLineChartTimePeriod");
        const checkboxCompaniesIncludedSmallLine = model.getByName<UICheckbox>("checkboxCompaniesIncludedSmallLine");
        const customIndicesIncludedSmallLine = model.getByName<UICheckbox>("customIndicesIncludedSmallLine");
        const constantLinesIncludedSmallLine = model.getByName<UICheckbox>("constantLinesIncludedSmallLine");

        const companiesNames = checkboxCompaniesIncludedSmallLine.value;
        const selectedCompanies = companiesNames
          .map((companyName: string) => {
            const companyData: Company = findCompanyByName(companyName);
            if (companyData) {
              return {
                name: companyData.companyNameAndTicker,
                ticker: companyData.company.ticker,
                exchange: companyData.company.exchange,
              };
            } else {
              return null;
            }
          })
          .filter(Boolean);

        const filteredCustomIndices = Object.values(allPanelsData[ModelIndexes.CUSTOM_BUILD_INDEX]).filter((item) => {
          const panelItem = item as { indexName: string };
          return panelItem.indexName && customIndicesIncludedSmallLine.value.includes(panelItem.indexName);
        });

        const filteredConstantLines = Object.values(allPanelsData[ModelIndexes.CONSTANT_LINE]).filter((item) => {
          const lineItem = item as { lineName: string };
          return lineItem.lineName && constantLinesIncludedSmallLine.value.includes(lineItem.lineName);
        });

        tempValues = {
          ...tempValues,
          smallLineChartTimePeriod: smallLineChartTimePeriod.value,
          checkboxCompaniesIncludedSmallLine: selectedCompanies,
          customIndicesNames: customIndicesIncludedSmallLine.value,
          customIndices: filteredCustomIndices,
          constantLinesNames: constantLinesIncludedSmallLine.value,
          constantLines: filteredConstantLines,
        };
      }
      allPanelsData.push(tempValues);
    }

    if (i === ModelIndexes.CALL_OUT_BOXES) {
      let tempValues = {};

      if (isValidCallOutBoxes !== "Yes") {
        allPanelsData.push(tempValues);
        return;
      }

      if (isValidCallOutBoxes === "Yes") {
        const callOutBoxesTable = model.getByName<UITable>("callOutBoxesTable");
        tempValues = {
          ...tempValues,
          callOutBoxesTable: callOutBoxesTable.value,
        };
      }

      allPanelsData.push(tempValues);
    }

    if (i === ModelIndexes.BACKGROUND_SHADING_TABLE_PANEL) {
      let tempValues = {};
      if (isValidBackgroundShading !== "Yes") {
        allPanelsData.push(tempValues);
        return;
      }

      if (isValidBackgroundShading === "Yes") {
        const shadingPeriodTable = model.getByName<UITable>("shadingPeriodTable");
        tempValues = {
          ...tempValues,
          shadingPeriodTable: shadingPeriodTable.value,
        };
      }

      allPanelsData.push(tempValues);
    }

    if (i === ModelIndexes.VERTICAL_LINE_TABLE_PANEL) {
      let tempValues = {};
      if (isValidVerticalLine !== "Yes") {
        allPanelsData.push(tempValues);
        return;
      }

      if (isValidVerticalLine === "Yes") {
        const verticalLineTable = model.getByName<UITable>("verticalLineTable");
        tempValues = {
          ...tempValues,
          verticalLineTable: verticalLineTable.value,
        };
      }

      allPanelsData.push(tempValues);
    }

    if (i === ModelIndexes.FORMAT_TEMPLATE) {
      let tempValues = {};

      const formatTemplateSource = model.getByName<UICheckbox>("formatTemplateSource");
      const formatTemplateNotes = model.getByName<UICheckbox>("formatTemplateNotes");
      const presentationDesign = model.getByName<UIFormatTemplateSelection>("presentationDesign");

      const selectedPlaceholder = getSelectedShape();
      tempValues = {
        placeholder: selectedPlaceholder ?? {},
        formatId: presentationDesign.value,
        source: formatTemplateSource.value,
        notes: formatTemplateNotes.value,
      };
      allPanelsData.push(tempValues);
    }
  });

  return allPanelsData;
}

export function convertToRequiredFormat(rawData) {
  // Destructure the main sections by their known order/keys
  const [
    { chartType: rawChartType },
    { companies, timePeriod, currency, percentageChart, customBuildIndex, staticLine },
    { indexStartingPoint, company, value },
    rawIndicesSection,
    rawLinesSection,
    { tradingVolume, summaryTable, smallLineChart, callOutBoxes, backgroundShading, verticalLine },
    summarySection,
    smallLineSection,
    callOutSection,
    shadingSection,
    verticalSection,
    formatAndPlaceholderSection,
  ] = rawData;

  // 2. companies array
  const companiesOut = companies.map((c) => {
    const noSuffix = c.dataType.replace(/-\d+$/, "");
    const underscored = noSuffix.replace(/ /g, "_");

    return {
      id: String(c.id),
      ticker: c.company.ticker,
      exchange: c.company.exchange,
      dataType: underscored,
      name: c.companyNameAndTicker,
      category: c.lineCategory === "Primary Company" ? "primary" : "peer",
    };
  });

  // 3. date range
  const [startDate, endDate] = timePeriod;

  let percentageChangeChart = {};

  if (percentageChart) {
    if (indexStartingPoint === "Share Price" || indexStartingPoint === "Company Share Price") {
      percentageChangeChart = {
        indexStartingPoint: "share-price",
      };
    }

    if (indexStartingPoint === "Value") {
      percentageChangeChart = {
        indexStartingPoint: "value",
        value: value,
      };
    }
  }

  // 5. customBuiltIndex
  const customBuiltIndexArray = [];
  if (customBuildIndex) {
    ["index", "index1", "index2", "index3", "index4"].forEach((key) => {
      if (rawIndicesSection[key]) {
        const idx = rawIndicesSection[key];
        customBuiltIndexArray.push({
          id: idx.id,
          constituentContributionApproach: idx.constituentContributionApproach.toLowerCase().replace(/\s+/g, "-"),
          indexName: idx.indexName,
          contituents: idx.defineIndexContituents.map((item) => ({
            ticker: item.company.ticker,
            name: item.companyNameAndTicker,
            exchange: item.company.exchange,
            id: item.id,
          })),
        });
      }
    });
  }

  // 6. constantValueLines
  const constantValueLines = [];
  if (staticLine) {
    ["line", "line1", "line2", "line3", "line4"].forEach((key) => {
      const l = rawLinesSection[key];
      if (l) {
        if (l.calculationApproach === "Input Value") {
          constantValueLines.push({
            id: l.id,
            calculationApproach: "input-value",
            lineName: l.lineName,
            value: l.value,
          });
        }
        if (l.calculationApproach === "Derived Value") {
          if (l.derivedDataCalculationApproach === "Time Series") {
            constantValueLines.push({
              id: l.id,
              calculation: l.calculation?.toLowerCase(),
              calculationApproach: "derived-value",
              company: companies.find((c) => c.companyNameAndTicker === l.company.name).id.toString(),
              derivedDataCalculationApproach: l.derivedDataCalculationApproach.toLowerCase().replace(/\s+/g, "-"),
              lineName: l.lineName,
              timePeriod: {
                start: l.timePeriod?.[0],
                end: l.timePeriod?.[1],
              },
            });
          }
          if (l.derivedDataCalculationApproach === "Point-in-Time") {
            constantValueLines.push({
              id: l.id,
              calculationApproach: "derived-value",
              company: companies.find((c) => c.companyNameAndTicker === l.company.name).id.toString(),
              derivedDataCalculationApproach: l.derivedDataCalculationApproach.toLowerCase().replace(/\s+/g, "-"),
              lineName: l.lineName,
              date: l.Date ? new Date(l.Date).toISOString().split("T")[0] : null,
              premium: Number(l.premiumToDate).toFixed(2),
            });
          }
        }
      }
    });
  }

  // 7. summaryTable
  let summaryTableData = {};
  if (summaryTable) {
    const { rowDataCategory, summaryPeriodTable, summaryCompaniesIndices, customIndices } = summarySection;
    const summaryPeriodData = summaryPeriodTable.map((item) => {
      const [startDate, endDate] = item.dateRange.split(" -> ");
      return {
        id: item.id,
        periodName: item.periodName,
        startDate,
        endDate,
      };
    });
    summaryTableData = {
      rowDataCategory: rowDataCategory.toLowerCase().replace(/\s+/g, "").replace("/", "-"),
      periods: summaryPeriodData,
      companyIndices: getMatchedCompanies(companies, summaryCompaniesIndices),
      customBuiltIndexes: customIndices.map((item) => item.id),
    };
  }

  // 8. smallLineChart
  let smallLineChartData = {};
  if (smallLineChart) {
    const {
      smallLineChartTimePeriod,
      customIndices: customIndecisSmallLineChart,
      checkboxCompaniesIncludedSmallLine,
      constantLines,
    } = smallLineSection;
    smallLineChartData = {
      startDate: smallLineChartTimePeriod[0],
      endDate: smallLineChartTimePeriod[1],
      companyIndices: getMatchedCompanies(companies, checkboxCompaniesIncludedSmallLine),
      customBuiltIndexes: customIndecisSmallLineChart.map((item) => item.id),
      constantValueLines: constantLines.map((item) => item.id),
    };
  }
  // 9. callOutBoxes
  let callOutBoxesData = [];
  if (callOutBoxes) {
    callOutBoxesData = callOutSection.callOutBoxesTable.map((c) => ({
      id: c.id,
      company: companies.find((co) => co.company.ticker === c.company.ticker).id.toString(),
      text: c.text,
      date: c.date,
    }));
  }

  // 10. shadingPeriods
  let shadingPeriodsData = [];
  if (backgroundShading) {
    shadingPeriodsData = shadingSection.shadingPeriodTable.map((s) => ({
      id: s.id,
      label: s.label,
      startDate: s.dateRange.split(" -> ")[0],
      endDate: s.dateRange.split(" -> ")[1],
    }));
  }
  // 11. verticalLines
  let verticalLinesData = [];
  if (verticalLine) {
    verticalLinesData = verticalSection.verticalLineTable.map((v) => ({
      id: v.id,
      label: v.label,
      date: v.date,
    }));
  }

  // 12. formatAndPlaceholderSection

  const { formatId, metadata } = formatAndPlaceholderSection;
  const placeholder = getSelectedShape();

  return {
    chartType: rawChartType,
    companies: companiesOut,
    startDate,
    endDate,
    currency,
    percentageChangeChart,
    customBuiltIndex: customBuiltIndexArray,
    constantValueLines,
    tradingVolume: tradingVolume,
    summaryTable: summaryTableData,
    smallLineChart: smallLineChartData,
    callOutBoxes: callOutBoxesData,
    shadingPeriods: shadingPeriodsData,
    verticalLines: verticalLinesData,
    formatId,
    placeholder,
    metadata,
  };
}

function getMatchedCompanies(companies, summaryCompaniesIndices) {
  const namesSet = new Set(summaryCompaniesIndices.map((c) => c.name));
  return companies.filter((c) => namesSet.has(c.companyNameAndTicker)).map((c) => c.id.toString());
}

export function getAllModelsData() {
  const allModelsData = localStorage.getItem("PanelsData");
  const { models } = useSharePricePerformanceStore.getState();
  let data = JSON.parse(allModelsData);
  const model: Model = models[ModelIndexes.FORMAT_TEMPLATE];
  let tempValues = {};

  const presentationDesign = model.getByName<UIFormatTemplateSelection>("presentationDesign");
  const formatTemplateSource = model.getByName<UICheckbox>("formatTemplateSource");
  const formatTemplateNotes = model.getByName<UICheckbox>("formatTemplateNotes");

  tempValues = {
    formatId: presentationDesign.value,
    placeholder: data[ModelIndexes.FORMAT_TEMPLATE].placeholder ?? {},
    metadata: {
      source: formatTemplateSource?.value?.length > 0,
      notes: formatTemplateNotes?.value?.length > 0,
    },
  };

  data[ModelIndexes.FORMAT_TEMPLATE] = tempValues;
  return data;
}
